import { useEffect, useMemo, useState } from 'react';
import type { CallCenterOrder, CallCenterPaymentType } from '@matix/types';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { giftCardsAPI } from '@matix/api-client';
import { callCenterApi, ccErrorMessage } from './api';
import { useMoneyFormatter } from './hooks';
import { useCallCenter } from './store';

/**
 * Take the money for a call-centre order at hand-over.
 *
 * Deliberately NOT the till's PaymentModal. That modal settles the CART — it
 * reads the cart store, it offers loyalty redemption, gift-card tender, Tap,
 * unpaid-on-account and the discount path, and every one of those is either
 * meaningless here (the order is already written) or actively wrong (a second
 * discount on a total the customer was quoted on the phone). This dialog does
 * one thing: split an already-known total across tenders and post it.
 *
 * The split is the reason it takes an array rather than one method. A customer
 * at the door handing over a note and asking to put the rest on a card is
 * ordinary, and a receipt that records only half of it is a drawer that will
 * not reconcile at close.
 */
export function SettleDialog({
  order,
  paymentTypes,
  onClose,
  onSettled,
}: {
  order: CallCenterOrder | null;
  paymentTypes: CallCenterPaymentType[];
  onClose: () => void;
  onSettled: (order: CallCenterOrder) => void;
}): JSX.Element | null {
  const { t, lang } = useLang();
  const money = useMoneyFormatter();

  const [splits, setSplits] = useState<Array<{ payment_type_id: number; amount: number }>>([]);
  const [busy, setBusy] = useState(false);
  // Cards named against the gift-card splits. Kept alongside rather than on the
  // split, because one split is one tender line and a customer can present two
  // cards against it.
  const [cards, setCards] = useState<
    Array<{ card_number: string; pin: string | null; amount: number }>
  >([]);
  const [cardEntry, setCardEntry] = useState<{ number: string; pin: string } | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  const giftCardCode = useCallCenter((s) => s.bootstrap?.giftCardPaymentCode ?? null);

  useEffect(() => {
    setSplits([]);
    setCards([]);
    setCardEntry(null);
    setBusy(false);
  }, [order?.id]);

  const paid = useMemo(() => splits.reduce((sum, s) => sum + s.amount, 0), [splits]);
  const remaining = order ? round2(order.totalAmount - paid) : 0;

  if (!order) return null;

  function addSplit(type: CallCenterPaymentType): void {
    if (!order) return;
    // Default to the whole remaining balance — the overwhelmingly common case
    // is one tender for the whole thing, and making that a single tap is the
    // difference between this dialog being used and being worked around.
    const amount = round2(Math.max(0, remaining));
    if (amount <= 0 && splits.length > 0) return;
    setSplits((prev) => [...prev, { payment_type_id: type.id, amount }]);
  }

  function setSplitAmount(index: number, amount: number): void {
    setSplits((prev) => prev.map((s, i) => (i === index ? { ...s, amount: Math.max(0, amount) } : s)));
  }

  function removeSplit(index: number): void {
    const split = splits[index];
    setSplits((prev) => prev.filter((_, i) => i !== index));
    // Release the card that split was claimed against, or the server would see
    // cards naming more than the tender and refuse the whole settlement.
    if (split && giftCardCode != null) {
      const type = paymentTypes.find((p) => p.id === split.payment_type_id);
      if (type?.code === giftCardCode) {
        setCards((prev) => {
          const at = prev.findIndex((c) => Math.abs(c.amount - split.amount) < 0.001);
          return at === -1 ? prev : prev.filter((_, i) => i !== at);
        });
      }
    }
  }

  const balanced = Math.abs(remaining) <= 0.01 && splits.length > 0;

  /**
   * Look the card up before claiming anything against it.
   *
   * Read-only preview — the authoritative check runs again under lock inside
   * the settlement transaction, so a lookup that goes stale fails the settle
   * with a precise error rather than over-spending the card.
   */
  async function applyCard(): Promise<void> {
    if (!order || !cardEntry || giftCardCode == null) return;
    const type = paymentTypes.find((p) => p.code === giftCardCode);
    if (!type) return;
    setCardBusy(true);
    try {
      const look = await giftCardsAPI.lookup({
        card_number: cardEntry.number.trim(),
        pin: cardEntry.pin.trim() || null,
        amount: round2(Math.max(0, remaining)),
      });
      if (!look.ok) {
        toast(
          t({ en: `Gift card refused (${look.reason ?? 'unknown'})`, ar: `البطاقة مرفوضة (${look.reason ?? 'غير معروف'})` }),
          'error',
        );
        return;
      }
      const amount = round2(Math.min(look.balance, Math.max(0, remaining)));
      if (amount <= 0) {
        toast(t({ en: 'That card has no balance left.', ar: 'لا يوجد رصيد في هذه البطاقة.' }), 'error');
        return;
      }
      setCards((prev) => [
        ...prev,
        { card_number: cardEntry.number.trim(), pin: cardEntry.pin.trim() || null, amount },
      ]);
      setSplits((prev) => [...prev, { payment_type_id: type.id, amount }]);
      setCardEntry(null);
    } catch (err) {
      toast(ccErrorMessage(err, 'Could not check the card'), 'error');
    } finally {
      setCardBusy(false);
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!order || !balanced) return;
    setBusy(true);
    try {
      const updated = await callCenterApi.branch.settle(order.id, splits, {
        markDelivered: true,
        giftCards: cards,
      });
      onSettled(updated);
      toast(
        t({
          en: `Order #${updated.entryNo ?? updated.receiptId} settled and delivered`,
          ar: `تم تحصيل وتسليم الطلب #${updated.entryNo ?? updated.receiptId}`,
        }),
        'success',
      );
      onClose();
    } catch (err) {
      toast(ccErrorMessage(err, 'Failed to settle'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const name =
    (lang === 'ar' ? order.customerNameAr || order.customerName : order.customerName) ?? '';

  return (
    <div className="cc-modal-backdrop" onClick={onClose}>
      <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="cc-modal-head">
          <div>
            <div className="cc-modal-title">
              {t({ en: 'Take payment', ar: 'تحصيل الدفع' })}
            </div>
            <div className="cc-modal-sub">
              #{order.entryNo ?? order.receiptId} · {name}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="cc-settle-total">
          <span>{t({ en: 'Total due', ar: 'المستحق' })}</span>
          <strong>{money(order.totalAmount)}</strong>
        </div>

        <div className="cc-settle-methods">
          {/*
            Gift cards get their OWN button rather than hijacking whichever
            tender the tenant nominated. That nomination is a code, and on at
            least one live tenant it is code 1 — Cash. Turning the Cash button
            into a card-number prompt would break the most common action at the
            counter because of a configuration mistake somewhere else. The split
            still lands on the nominated payment type, exactly as the order path
            books it; only the way the cashier reaches it is separate.
          */}
          {giftCardCode != null && (
            <button
              type="button"
              className="cc-settle-method cc-settle-method--gift"
              onClick={() => setCardEntry({ number: '', pin: '' })}
            >
              {t({ en: 'Gift card', ar: 'بطاقة هدايا' })}
            </button>
          )}
          {paymentTypes.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`cc-settle-method ${p.isCash ? 'cc-settle-method--cash' : ''}`}
              onClick={() => addSplit(p)}
            >
              {lang === 'ar' ? p.nameAr || p.name : p.name}
            </button>
          ))}
        </div>

        {cardEntry && (
          <div className="cc-inline-form">
            <label className="cc-field">
              <span>{t({ en: 'Gift card number', ar: 'رقم بطاقة الهدايا' })}</span>
              <input
                autoFocus
                value={cardEntry.number}
                onChange={(e) => setCardEntry({ ...cardEntry, number: e.target.value })}
              />
            </label>
            <label className="cc-field">
              <span>{t({ en: 'PIN', ar: 'الرمز' })}</span>
              <input
                value={cardEntry.pin}
                onChange={(e) => setCardEntry({ ...cardEntry, pin: e.target.value })}
              />
            </label>
            <div className="cc-inline-form-actions">
              <button type="button" className="cc-btn" onClick={() => setCardEntry(null)}>
                {t({ en: 'Cancel', ar: 'إلغاء' })}
              </button>
              <button
                type="button"
                className="cc-btn cc-btn--primary"
                disabled={cardBusy || !cardEntry.number.trim()}
                onClick={() => void applyCard()}
              >
                {cardBusy ? t({ en: 'Checking…', ar: 'جارٍ التحقق…' }) : t({ en: 'Apply card', ar: 'استخدام البطاقة' })}
              </button>
            </div>
          </div>
        )}

        {splits.length > 0 && (
          <ul className="cc-settle-splits">
            {splits.map((s, i) => {
              const type = paymentTypes.find((p) => p.id === s.payment_type_id);
              return (
                <li key={`${s.payment_type_id}-${i}`}>
                  <span>{type ? (lang === 'ar' ? type.nameAr || type.name : type.name) : '—'}</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={s.amount}
                    onChange={(e) => setSplitAmount(i, Number(e.target.value) || 0)}
                  />
                  <button type="button" onClick={() => removeSplit(i)} title={t({ en: 'Remove', ar: 'حذف' })}>
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className={`cc-settle-balance ${balanced ? 'cc-settle-balance--ok' : ''}`}>
          <span>{t({ en: 'Remaining', ar: 'المتبقي' })}</span>
          <strong>{money(remaining)}</strong>
        </div>

        <button
          type="button"
          className="cc-send"
          disabled={!balanced || busy}
          onClick={() => void handleConfirm()}
        >
          {busy
            ? t({ en: 'Posting…', ar: 'جارٍ الترحيل…' })
            : t({ en: 'Confirm & mark delivered', ar: 'تأكيد وتعليم كمُسلّم' })}
        </button>

        <p className="cc-modal-foot">
          {t({
            en: 'This records the tender on the existing receipt and puts the cash in your open shift. No second receipt is created.',
            ar: 'يسجل هذا طريقة الدفع على الفاتورة الحالية ويضع النقدية في ورديتك المفتوحة. لا يتم إنشاء فاتورة ثانية.',
          })}
        </p>
      </div>
    </div>
  );
}

/**
 * Two decimals, on the UI side only.
 *
 * The server is authoritative for money and rounds at the tenant's currency
 * precision; this exists purely so the "remaining" line does not show
 * 0.30000000000000004 while an agent decides whether it is settled. The
 * balance check that gates the button is a 0.01 tolerance for the same reason.
 */
function round2(n: number): number {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}
